import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { ActionIcon, Textarea } from "@mantine/core"
import { IconArrowUp } from "@tabler/icons-react"
import { useCallback } from "react"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * The question composer — an autosizing textarea plus a send button. Enter sends;
 * Shift+Enter inserts a newline. Input + send are disabled while a question is in
 * flight (`busy`). Pure presentation: the smart wrapper owns the value and the
 * submit side effect.
 */
export const AskComposer = ({
  value,
  onChange,
  onSubmit,
  busy = false,
  autoFocus = false,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const canSend = value.trim().length > 0 && !busy

  const submit = useCallback((): void => {
    if (value.trim().length === 0 || busy) return
    onSubmit()
  }, [value, busy, onSubmit])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      // Enter sends; Shift+Enter inserts a newline. Skip the IME composition
      // Enter (keystroke that commits a candidate) so CJK input isn't submitted.
      if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return
      event.preventDefault()
      submit()
    },
    [submit],
  )

  return (
    <div className={classes.composer} data-ask-composer="">
      <Textarea
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        onKeyDown={handleKeyDown}
        placeholder={i18n._(msg`Ask anything about your meetings…`)}
        aria-label={i18n._(msg`Ask a question about your meetings`)}
        autosize
        minRows={1}
        maxRows={6}
        disabled={busy}
        classNames={{ root: classes.inputRoot, input: classes.input }}
        data-autofocus={autoFocus || undefined}
        data-testid="ask-composer-input"
      />
      <ActionIcon
        onClick={submit}
        disabled={!canSend}
        loading={busy}
        size="lg"
        radius="xl"
        variant="filled"
        color="brand"
        className={classes.send}
        aria-label={i18n._(msg`Send`)}
        title={i18n._(msg`Send`)}
        data-testid="ask-composer-send"
      >
        <IconArrowUp size={18} />
      </ActionIcon>
    </div>
  )
}
